import EzController from 'controllers/ez-base-controller';
import Cookies from 'js-cookie';
import { ajaxGet, getAjaxModal, insertAjaxContent } from 'lib/ajax-helpers';
import { ezConfirm } from 'lib/modal_helpers';
import {
  splitTestEnabled,
  triggerCustomEvent,
  primeForPostLogin,
  featureEnabled,
} from 'lib/helpers';
import { downloadResource } from 'lib/download-helpers';
import events from 'lib/events';
import { addSessionDownloadActivity } from 'lib/sessions-manager';
import {
  sendTrackingEvent,
  findCategoryByElement,
  trackCompleteEvent,
  NEW_ANALYTICS_FEATURE,
} from 'components/analytics/conversions/tracking';
import { JOIN_TRIGGERED, JOIN_PRO_TRIGGERED, JOIN_PRO_COMPLETE } from 'components/analytics/conversions/constants/actions';
import axios from 'axios';
import { ezLoadingBtnOn, ezLoadingBtnOff } from 'ez-styles/assets/js/utils/buttons';

export default class DownloadButtonController extends EzController {
  // Let's avoid granny double clicks by only re-enabling the button after JS is done.
  clickProcessing = false;

  initialize() {
    if (document.body.dataset.userSignedIn === 'true') {
      this.disableDownloadLimitModal();
    } else if (document.body.dataset.downloadLimitModalTest === 'true') {
      // split test: download_limit_modal
      this.disableDownloadLimitModal();
    }

    window.addEventListener('DownloadLimitModalSeen', this.disableDownloadLimitModal);
  }

  connect() {
    this.mainDlBtn = this.element;

    if (!this.isTurbolinksPreview) {
      const searchData = window.localStorage.getItem(`resource-${this.resourceId}`);
      if (searchData) {
        this.searchData = JSON.parse(searchData);
        window.localStorage.removeItem(`resource-${this.resourceId}`);
      }
      this.flagLoaded();

      if (splitTestEnabled('preview-image-downloads') && this.isSidebarDownloadButton) {
        window.addEventListener(events.show_page.preview_image_click, this.previewImageTriggerClick);
      }

      this.checkDownloadCookie();
    }
  }

  disconnect() {
    window.removeEventListener('DownloadLimitModalSeen', this.disableDownloadLimitModal);

    if (splitTestEnabled('preview-image-downloads')) {
      window.removeEventListener(events.show_page.preview_image_click, this.clickSelf);
    }
  }

  previewImageTriggerClick = () => {
    this.sendDownloadEvent();

    if (this.resourceLicense === 'editorial') {
      this.freeDownload();
    } else {
      getAjaxModal(this.element.href);
    }
  }

  get resourceLicense() {
    return document.querySelector('.ez-resource-show').dataset.resourceType;
  }

  get isSidebarDownloadButton() {
    return this.element === document.querySelector('.ez-resource-show__sidebar__inner .ez-btn');
  }

  proJoin = (event) => {
    Cookies.set('download_size_for_checkout', event.currentTarget.dataset.size);
  }

  proDownload = (event = null) => {
    if (event) event.preventDefault();
    if (this.clickProcessing === true) return;
    this.clickProcessing = true;

    const downloadSize = this.getDownloadSize(event, event?.currentTarget);

    this.triggerDownload(event, { size: downloadSize });
    this.clickProcessing = false;
  }

  freeDownload = (event = null) => {
    if (event) event.preventDefault();
    if (this.clickProcessing === true) return;
    this.clickProcessing = true;

    const downloadSize = this.getDownloadSize(event, event?.currentTarget);

    if (this.hasHitDownloadLimit() && !window.disableDownloadLimitModal) {
      if (this.element.dataset.dlAdModalPath) {
        this.setDownloadCookie(this.element.dataset.dlAdModalPath, downloadSize);
      }

      this.openDownloadLimitModal();
    } else if (this.element.dataset.dlAdModalPath) {
      this.showDownloadAdPopup();

      if (downloadSize !== 'original') {
        const downloadFileUrl = this.getDownloadFileUrl(event);

        this.processDlSize({ url: downloadFileUrl, size: downloadSize });
      }

      if (this.element.getAttribute('data-license-type') !== 'editorial') {
        this.showFreeAttributionSlidedown();
      }
    } else {
      this.triggerDownload(event, { size: downloadSize });
    }

    this.clickProcessing = false;
  }

  getDownloadSize(event, buttonTarget = null) {
    const defaultSize = 'original';

    if (!event) return defaultSize;

    if (typeof event.detail.dlSize !== 'undefined') return event.detail.dlSize;

    if (buttonTarget && buttonTarget.dataset && (typeof buttonTarget.dataset.size !== 'undefined')) {
      return buttonTarget.dataset.size;
    }

    return defaultSize;
  }

  getDownloadFileUrl(event = null) {
    const defaultUrl = this.element.getAttribute('href');

    if (!event) return defaultUrl;

    if (typeof event.detail.downloadFileUrl !== 'undefined') return event.detail.downloadFileUrl;

    return defaultUrl;
  }

  showFreeAttributionSlidedown() {
    // first, get rid of any existing slidedowns
    if (document.querySelector('[data-controller="free-attribution-slidedown"]')) {
      const existingSlidedown = document.querySelector('[data-controller="free-attribution-slidedown"]');

      existingSlidedown.remove();
    }

    const resourceId = document.querySelector('[data-controller*="resource-show-main"]').getAttribute('data-resource-id');

    let slidedownParent;

    // start split-test: resource_show_modal_view
    if (document.querySelector('[data-controller="resource-show-modal-view"]')) {
      slidedownParent = document.querySelector('[data-controller="resource-show-modal-view"]');
    } else {
      slidedownParent = document.querySelector('.ez-page-wrapper__content');
    }
    // end split-test: resource_show_modal_view

    insertAjaxContent(`/resources/${resourceId}/show_free_attr_slidedown`,
      slidedownParent,
      'afterbegin',
      () => {
        triggerCustomEvent(events.show_page.reveal_free_attribution_slidedown);
      });
  }

  disableDownloadLimitModal = () => {
    window.disableDownloadLimitModal = true;
  }

  get curPopCount() {
    return parseInt(Cookies.get('pop_count'), 10) || 0;
  }

  set curPopCount(value) {
    Cookies.set('pop_count', value);
  }

  hasHitDownloadLimit() {
    return this.curPopCount >= 3;
  }

  triggerDownload = (event, { size = 'original', reloadPage = false } = {}) => {
    addSessionDownloadActivity(this.resourceId);

    const downloadSize = event?.detail?.size ? event.detail.size : size;
    const downloadFileUrl = this.getDownloadFileUrl(event);

    if (!window.disableDownloadLimitModal) {
      this.curPopCount += 1;
    }

    if (downloadSize === 'original' || this.dlPopupReady) {
      ezLoadingBtnOn(this.mainDlBtn);
      this.deliverFile({ url: downloadFileUrl, downloadSize, reloadPage });
    } else {
      this.processDlSize({ url: downloadFileUrl, size: downloadSize, reloadPage });
    }
  }

  processDlSize({ url, size, reloadPage = false }) {
    ezLoadingBtnOn(this.mainDlBtn);

    ajaxGet(url,
      async (response) => {
        const s3Response = await this.pollForConvertedDlSize({
          fn: this.checkSizeConversion,
          fnCondition: this.urlCheck,
          jobId: response.data.job_id,
          interval: 1000,
        });

        this.setS3ResponseUrlOrDeliver({ url: s3Response.data.url, size, reloadPage });
      },
      (error) => {
        console.error(error); // eslint-disable-line no-console
      },
      {
        params: { size },
      });
  }

  setS3ResponseUrlOrDeliver({ url, size, reloadPage = false }) {
    if (document.body.querySelector('[data-controller="download-popup"]')) {
      const dlAdModal = document.body.querySelector('[data-controller="download-popup"]');

      this.dlPopupReady = true;

      dlAdModal.setAttribute('data-download-file-url', url);
    } else {
      this.deliverFile({ url, downloadSize: size, reloadPage });
    }
  }

  urlCheck = (result) => typeof result.data.url === 'undefined'

  pollForConvertedDlSize = async ({
    fn, fnCondition, jobId, interval,
  }) => {
    let result = await fn(jobId);

    /* eslint-disable no-await-in-loop */
    while (fnCondition(result)) {
      await this.waitForConvertedDlSize(interval);
      result = await fn(jobId);
    }
    /* eslint-enable no-await-in-loop */

    return result;
  }

  waitForConvertedDlSize = (interval = 1000) => new Promise((resolve) => {
    setTimeout(resolve, interval);
  })

  checkSizeConversion = (jobId) => axios.get(`/api/v2/resource_conversion/${jobId}`);

  deliverFile({ url, downloadSize = 'original', reloadPage = false }) {
    triggerCustomEvent(events.show_page.file_delivered);

    downloadResource({ url, downloadSize, reloadPage });

    if (this.mainDlBtn.classList.contains('has-spinner')) {
      const dlBtn = this.mainDlBtn;

      if (downloadSize === 'original') {
        setTimeout(() => {
          ezLoadingBtnOff(dlBtn);
          if (document.querySelector('.ez-drop-down--download-btn')) {
            document.querySelector('.ez-drop-down--download-btn').classList.add('is-closed');
          }
        }, 1000);
      } else {
        ezLoadingBtnOff(dlBtn);
      }
    }
  }

  showDownloadAdPopup() {
    const downloadAdModalPath = this.element.dataset.dlAdModalPath;

    this.element.setAttribute('remote', 'true');

    window.addEventListener('TriggerDownload', this.triggerDownload, { once: true });

    getAjaxModal(downloadAdModalPath);
  }

  setDownloadCookie(dlPopupLink, downloadSize) {
    primeForPostLogin('download_after_social_signin', dlPopupLink);
    Cookies.set('download_size', downloadSize);
  }

  checkDownloadCookie() {
    const dlPopupLink = Cookies.get('download_after_social_signin');
    const downloadSize = Cookies.get('download_size');

    if (dlPopupLink && document.body.dataset.userSignedIn === 'true') {
      const urlParams = new URLSearchParams(window.location.search);

      if (!urlParams.has('autodl_token')) {
        this.element.setAttribute('remote', 'true');
        window.addEventListener('TriggerDownload', this.triggerDownload, { once: true });

        if (downloadSize && downloadSize !== 'original') {
          const downloadFileUrl = document.querySelector('.ez-resource-show__sidebar__download-options').dataset.downloadUrl;

          this.processDlSize({ url: downloadFileUrl, size: downloadSize });
        }

        getAjaxModal(dlPopupLink);
      }
    }

    Cookies.remove('download_after_social_signin');
    Cookies.remove('download_size');
  }

  openDownloadLimitModal() {
    const downloadLimitModalPath = this.element.dataset.dlLimitModalPath;

    this.element.setAttribute('remote', 'true');

    if (featureEnabled(NEW_ANALYTICS_FEATURE)) {
      sendTrackingEvent({
        element: this.element,
        data: {
          action: JOIN_TRIGGERED,
          category: `Trigger-Download_Limit-${this.curPopCount}`,
        },
      });
    }

    getAjaxModal(downloadLimitModalPath);
  }

  spendCreditAndDownload = ({ event, downloadSize }) => {
    // Need to reload the page so a new download button gets rendered
    this.triggerDownload(event, { size: downloadSize, reloadPage: true });

    this.creditSpent = true;
  }

  confirmCreditSpend(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const downloadSize = event.currentTarget.dataset.size || 'original';

    if (this.creditSpent) {
      this.triggerDownload(event, { size: downloadSize });
    } else {
      ezConfirm(
        event.currentTarget.getAttribute('data-confirm-text'),
        this.spendCreditAndDownload,
        ['Okay', 'Cancel'],
        { callbackArgs: { event, downloadSize } },
      );
    }
  }

  get isTurbolinksPreview() {
    return document.documentElement.hasAttribute('data-turbolinks-preview');
  }

  get resourceId() {
    return document.querySelector('.ez-resource-show').dataset.resourceId;
  }

  get resourceType() {
    return document.querySelector('.ez-resource-show').dataset.resourceType;
  }

  joinProClick(event) {
    this.sendDownloadEvent();

    if (!featureEnabled(NEW_ANALYTICS_FEATURE)) return;

    const category = findCategoryByElement(event.target);

    sendTrackingEvent({
      element: event.target,
      data: {
        action: JOIN_PRO_TRIGGERED,
        category,
        label: 'Pro',
      },
    });

    trackCompleteEvent({
      action: JOIN_PRO_COMPLETE,
      category,
    });
  }

  sendDownloadEvent() {
    let eventDetails = {
      resourceId: this.resourceId,
      resourceType: this.resourceType,
    };

    if (this.searchData) {
      eventDetails = Object.assign(eventDetails, this.searchData);
    }

    triggerCustomEvent(events.download_btn.initial_click, eventDetails);
  }
}
